/**
 * Mutations for board-row comments, all through the generated `TrackersService`.
 *
 * Each invalidates the row's comment list. Only the two that change how many
 * comments a row has also invalidate the board-level badge summary, which is a
 * count and a newest-comment time: editing or settling a comment moves neither,
 * and refetching it for every badge on the board would be work for no change.
 */
import { TrackersService } from '@inference.engine/workspace-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { workspaceHttp } from '@/lib/api/http-client';

import type { TrackerCommentResponseDto } from '../model/types';
import { TRACKER_COMMENT_QUERY_KEYS } from './query-keys';

export interface UseCreateTrackerCommentOptions {
  trackerId: string;
  rowKey: string;
}
export interface CreateTrackerCommentVariables {
  content: Record<string, unknown>;
  /** Set to reply to a top-level comment. Threads are one level deep. */
  parentId?: string;
}

export function useCreateTrackerCommentMutation({
  trackerId,
  rowKey,
}: UseCreateTrackerCommentOptions) {
  const queryClient = useQueryClient();
  return useMutation<TrackerCommentResponseDto, Error, CreateTrackerCommentVariables>({
    mutationFn: async ({ content, parentId }) => {
      const response = await TrackersService.createComment({
        client: workspaceHttp,
        path: { trackerId },
        body: { rowKey, content, parentId },
      });
      if (!response.data) throw new Error('Failed to create comment');
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRACKER_COMMENT_QUERY_KEYS.list(trackerId, rowKey),
      });
      void queryClient.invalidateQueries({
        queryKey: TRACKER_COMMENT_QUERY_KEYS.summary(trackerId),
      });
    },
  });
}

export interface UseUpdateTrackerCommentOptions {
  trackerId: string;
  rowKey: string;
  commentId: string;
}
export interface UpdateTrackerCommentVariables {
  /** Editing the body is author-only; settling one is open to anyone. */
  content?: Record<string, unknown>;
  resolved?: boolean;
}

export function useUpdateTrackerCommentMutation({
  trackerId,
  rowKey,
  commentId,
}: UseUpdateTrackerCommentOptions) {
  const queryClient = useQueryClient();
  return useMutation<TrackerCommentResponseDto, Error, UpdateTrackerCommentVariables>({
    mutationFn: async ({ content, resolved }) => {
      const response = await TrackersService.updateComment({
        client: workspaceHttp,
        path: { trackerId, commentId },
        body: { content, resolved },
      });
      if (!response.data) throw new Error('Failed to update comment');
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRACKER_COMMENT_QUERY_KEYS.list(trackerId, rowKey),
      });
    },
  });
}

export interface UseDeleteTrackerCommentOptions {
  trackerId: string;
  rowKey: string;
  commentId: string;
}

export function useDeleteTrackerCommentMutation({
  trackerId,
  rowKey,
  commentId,
}: UseDeleteTrackerCommentOptions) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await TrackersService.deleteComment({
        client: workspaceHttp,
        path: { trackerId, commentId },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRACKER_COMMENT_QUERY_KEYS.list(trackerId, rowKey),
      });
      void queryClient.invalidateQueries({
        queryKey: TRACKER_COMMENT_QUERY_KEYS.summary(trackerId),
      });
    },
  });
}
